import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  InputAdornment,
  Button,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { AddProduitModal } from "./ProduitModals";
import { ReactSession } from "react-client-session";
import { fetchBsgCode } from "./global/apiService";
import axios from "axios";
import config from "./global/config";
import ProductCard from "./ProductCard";

const ProductSelectionDialog = ({
  open,
  onClose,
  onSelect,
  categories = [],
  brands = [],
  products = [],
  onProductAdded,
  isFournisseur = false,
  isFacture = false,
}) => {
  // State Management
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [stockWarning, setStockWarning] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [bsgCodes, setBsgCodes] = useState([]);
  const [visiblePurchasePrices, setVisiblePurchasePrices] = useState(new Set());
  const [newProductData, setNewProductData] = useState({
    categorie_code: "",
    id_categorie: "",
    id_marque: "",
    designation: "",
    unite: "",
    stock_minimum: "",
    stock_actuel: "",
    garantie: "",
    prix_achat: "",
    prix_vente: "",
    tva: "",
    description: "",
  });

  // API Configuration
  const api = axios.create({
    baseURL: config.apiUrl,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  // Fetch BSG Codes
  useEffect(() => {
    const loadBsgCodes = async () => {
      try {
        const bsgCodesData = await fetchBsgCode();
        setBsgCodes(Array.isArray(bsgCodesData) ? bsgCodesData : []);
      } catch (error) {
        if (error.response?.status === 401) {
          window.location.href = "/login";
        }
        console.error("Error fetching BSG codes:", error);
        setBsgCodes([]);
      }
    };

    loadBsgCodes();
  }, []);

  // Filter products
  useEffect(() => {
    try {
      if (!products || typeof products !== "object") {
        console.error("Invalid products data:", products);
        setFilteredProducts([]);
        return;
      }

      const productsArray = Array.isArray(products) ? products : [products];

      let filtered = [...productsArray];

      if (selectedCategory) {
        filtered = filtered.filter(
          (product) => product.id_categorie === selectedCategory
        );
      }

      if (selectedBrand) {
        filtered = filtered.filter(
          (product) => product.id_marque === selectedBrand
        );
      }

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(
          (product) =>
            (product.designation &&
              product.designation.toLowerCase().includes(searchLower)) ||
            (product.reference &&
              product.reference.toLowerCase().includes(searchLower)) ||
            (product.description &&
              product.description.toLowerCase().includes(searchLower))
        );
      }

      setFilteredProducts(filtered);
    } catch (error) {
      console.error("Error processing products:", error);
      setFilteredProducts([]);
    }
  }, [selectedCategory, selectedBrand, searchTerm, products]);

  // Calculate filtered brands
  const filteredBrands = useMemo(() => {
    try {
      if (!products || !Array.isArray(brands)) {
        return [];
      }

      const productsArray = Array.isArray(products) ? products : [products];

      return selectedCategory
        ? brands.filter((brand) => {
            const brandProducts = productsArray.filter(
              (product) => product.id_categorie === selectedCategory
            );
            return brandProducts.some(
              (product) => product.id_marque === brand.id_marque
            );
          })
        : [];
    } catch (error) {
      console.error("Error calculating filtered brands:", error);
      return [];
    }
  }, [selectedCategory, products, brands]);

  const handleUpdatePrices = async (productId, newPrices) => {
    try {
      const currentProduct = products.find((p) => p.id_produit === productId);

      if (!currentProduct) {
        throw new Error("Product not found");
      }

      const formData = new FormData();
      formData.append("METHOD", "PUT");

      formData.append("id_categorie", currentProduct.id_categorie);
      formData.append("categorie_code", currentProduct.categorie_code);
      formData.append("unite", currentProduct.unite);
      formData.append("tva", currentProduct.tva);
      formData.append("designation", currentProduct.designation);
      formData.append("description", currentProduct.description);
      formData.append("id_marque", currentProduct.id_marque);
      formData.append("stock_minimum", currentProduct.stock_minimum);
      formData.append("stock_actuel", currentProduct.stock_actuel);
      formData.append("garantie", currentProduct.garantie);

      formData.append("prix_achat", newPrices.prix_achat);
      formData.append("prix_vente", newPrices.prix_vente);
      formData.append("user_upd", ReactSession.get("username"));

      const response = await api.post(
        `produits.php?id_produit=${productId}`,
        formData
      );

      if (
        typeof response.data === "string" &&
        response.data.includes("Error")
      ) {
        throw new Error(response.data);
      }

      const updatedProducts = products.map((product) =>
        product.id_produit === productId
          ? { ...product, ...newPrices }
          : product
      );

      if (onProductAdded) {
        await onProductAdded();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
      alert("Error updating product prices. Please try again.");
      console.error("Error updating product prices:", error);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(newProductData).forEach((key) => {
        formData.append(key, newProductData[key]);
      });
      formData.append("user_cre", ReactSession.get("username"));
      formData.append("METHOD", "POST");

      const response = await api.post("produits.php", formData);

      if (
        typeof response.data === "string" &&
        response.data.includes("Error")
      ) {
        throw new Error(response.data);
      }

      if (
        Array.isArray(response.data) ||
        (typeof response.data === "object" && response.data.id_produit)
      ) {
        setAddModalOpen(false);
        setNewProductData({
          categorie_code: "",
          id_categorie: "",
          id_marque: "",
          designation: "",
          unite: "",
          stock_minimum: "",
          stock_actuel: "",
          garantie: "",
          prix_achat: "",
          prix_vente: "",
          tva: "",
          description: "",
        });

        if (onProductAdded) {
          await onProductAdded();
        }

        if (typeof response.data === "object" && response.data.id_produit) {
          onSelect(response.data);
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
      alert(error.message || "Error adding product. Please try again.");
      console.error("Error adding product:", error);
    }
  };

  const handleNewProductChange = (e) => {
    const { name, value } = e.target;
    setNewProductData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProductSelect = (product) => {
    if (product.stock_actuel <= 0 && !isFournisseur && !isFacture) {
      setStockWarning({
        message: `Le produit "${product.designation}" n'est pas en stock.`,
        type: "error",
      });
      return;
    }

    if (product.stock_actuel <= product.stock_minimum && !isFacture) {
      setStockWarning({
        message: `Attention: Stock faible pour "${product.designation}". Disponible: ${product.stock_actuel}`,
        type: "warning",
      });
    }

    onSelect(product);
    handleReset();
  };

  const handleReset = () => {
    setSelectedCategory("");
    setSelectedBrand("");
    setSearchTerm("");
    setStockWarning(null);
    setVisiblePurchasePrices(new Set());
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Sélectionner un Produit</span>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => setAddModalOpen(true)}
            size="small"
          >
            Nouveau Produit
          </Button>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            {stockWarning && (
              <Alert severity={stockWarning.type} sx={{ mt: 2 }}>
                {stockWarning.message}
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Catégorie"
                >
                  <MenuItem value="">Toutes les catégories</MenuItem>
                  {Array.isArray(categories) &&
                    categories.map((category) => (
                      <MenuItem
                        key={category.id_categorie}
                        value={category.id_categorie}
                      >
                        {category.nom_categorie}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Marque</InputLabel>
                <Select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  label="Marque"
                  disabled={!selectedCategory}
                >
                  <MenuItem value="">Toutes les marques</MenuItem>
                  {Array.isArray(filteredBrands) &&
                  filteredBrands.length > 0 ? (
                    filteredBrands.map((brand) => (
                      <MenuItem key={brand.id_marque} value={brand.id_marque}>
                        {brand.description}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Aucune marque disponible</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              placeholder="Rechercher par désignation, référence ou description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ maxHeight: 400, overflow: "auto" }}>
              <Stack spacing={1}>
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((product) => (
                    <ProductCard
                      key={product.id_produit}
                      product={product}
                      isFournisseur={isFournisseur}
                      visiblePurchasePrices={visiblePurchasePrices}
                      onToggleVisibility={(productId) => {
                        setVisiblePurchasePrices((prev) => {
                          const newSet = new Set(prev);
                          if (newSet.has(productId)) {
                            newSet.delete(productId);
                          } else {
                            newSet.add(productId);
                          }
                          return newSet;
                        });
                      }}
                      onSelect={handleProductSelect}
                      onUpdatePrices={handleUpdatePrices}
                    />
                  ))
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      Aucun produit trouvé
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReset}>Annuler</Button>
        </DialogActions>
      </Dialog>

      <AddProduitModal
        open={addModalOpen}
        handleClose={() => setAddModalOpen(false)}
        handleSubmit={handleAddProduct}
        produitData={newProductData}
        handleChange={handleNewProductChange}
        categorie={bsgCodes}
        marques={brands}
        categories={categories}
      />
    </>
  );
};

export default ProductSelectionDialog;
